<template>
  <Teleport to="body">
    <Transition name="app-sliding-panel">
      <div
        v-if="modelValue"
        class="app-sliding-panel"
        :class="{ 'app-sliding-panel--inline': !modal }"
        @mousedown.self="onBackdropClick">
        <div
          ref="containerRef"
          class="app-sliding-panel__container"
          :class="[positionClass, containerClass]"
          :role="containerRole"
          :aria-modal="isDialog ? 'true' : null"
          :aria-label="ariaLabel"
          :aria-labelledby="labelledbyId"
          tabindex="-1">
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
// Auto-generated ids for headings adopted as the dialog's accessible name.
let headingIdCounter = 0
</script>

<script setup>
import {
  computed, ref, watch, nextTick, onMounted, onBeforeUnmount
} from 'vue'
import useModalStack from '@/composables/useModalStack'
import useScrollLock from '@/composables/useScrollLock'
import useOverlayActivity from '@/composables/useOverlayActivity'
import { hostName } from '@/common/activityLog'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  // A non-modal panel is a drawer: the page behind it keeps scrolling,
  // Tab and clicks, and Escape belongs to it only while focus is inside.
  modal: {
    type: Boolean,
    default: true
  },
  position: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value)
  },
  backdropClose: {
    type: Boolean,
    default: true
  },
  containerClass: {
    type: [String, Array, Object],
    default: null
  },
  ariaLabel: {
    type: String,
    default: null
  },
  // CSS selector focused on close when the opening element no longer exists
  // (e.g. the opener dismissed itself while the panel was open).
  fallbackFocus: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const positionClass = computed(() => `app-sliding-panel__container--${props.position}`)

const activity = useOverlayActivity('panel', () => props.modelValue, { target: hostName() })

const close = (reason) => {
  activity.armClose(reason)
  emit('update:modelValue', false)
}

const onBackdropClick = () => {
  if (props.modal && props.backdropClose) close('backdrop')
}

const containerRef = ref(null)
const labelledbyId = ref(null)
let openerEl = null

// A dialog must have an accessible name — from the ariaLabel prop or the
// panel's first heading. Nameless panels keep their pre-dialog semantics.
const isDialog = computed(() => props.modal && Boolean(props.ariaLabel || labelledbyId.value))

const containerRole = computed(() => {
  if (!props.modal) return 'complementary'
  return isDialog.value ? 'dialog' : null
})

const adoptHeadingLabel = () => {
  if (props.ariaLabel) return
  // Content that renders its own dialog owns the semantics — adopting a
  // heading here would nest two modal dialogs with the same name.
  const heading = containerRef.value?.querySelector('[role="dialog"]') ?
    null :
    containerRef.value?.querySelector('h1, h2, h3, h4')
  if (!heading) {
    labelledbyId.value = null
    return
  }
  if (!heading.id) heading.id = `app-sliding-panel-title-${headingIdCounter += 1}`
  labelledbyId.value = heading.id
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

const focusablesInPanel = () => Array.from(containerRef.value?.querySelectorAll(FOCUSABLE_SELECTOR) || [])
  .filter((el) => !el.disabled && el.offsetParent !== null)

// Keep Tab cycling inside the open panel — the page behind the backdrop is
// visually inert, so focus must not wander through it.
const trapTab = (event) => {
  const focusables = focusablesInPanel()
  if (!focusables.length) {
    event.preventDefault()
    containerRef.value?.focus()
    return
  }
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement
  if (event.shiftKey && (active === first || active === containerRef.value)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

// Body lock is ref-counted across every overlay kind, so closing a modal
// stacked over this panel can't unlock the page behind it. Drawers don't lock.
useScrollLock(() => props.modelValue && props.modal)

// Manual mode: restoreFocus() must run while this panel is still on the
// stack (it asks whether it was the topmost), so leave() comes after it.
// Escape arrives through the stack's shared listener (topmost only); the
// local listener keeps the Tab trap and the drawer's focus-scoped Escape.
// Only modal panels stack: a drawer stays open alongside the page, so owning
// the top of the stack would strip Escape and the Tab trap from a modal below.
const { isTopmost, enter, leave: leaveStack } = useModalStack(null, { onEscape: () => close('escape') })

const enterStack = () => {
  if (props.modal) enter()
}

const handleKeydown = (event) => {
  if (!props.modelValue) return
  if (!props.modal) {
    if (event.key === 'Escape' && !event.repeat && containerRef.value?.contains(document.activeElement)) close('escape')
    return
  }
  // Only the topmost panel traps focus — Tab on a stacked overlay must not
  // cycle through the panels underneath it.
  if (!isTopmost()) return
  if (event.key === 'Tab') trapTab(event)
}

const canRestore = (el) => el && el !== document.body &&
  el !== document.documentElement && el.isConnected

const restoreFocus = () => {
  if (!openerEl) return
  const wasTopmost = isTopmost()
  const active = document.activeElement
  const focusIsOurs = active === document.body ||
    containerRef.value?.contains(active) || !active?.isConnected
  // Don't steal focus from an overlay stacked above this panel — nor, for a
  // drawer, from the live page the user may be typing in.
  if ((props.modal && wasTopmost) || focusIsOurs) {
    if (canRestore(openerEl)) {
      openerEl.focus()
    } else if (props.fallbackFocus) {
      // The opener was <body> (focus race) or removed itself while the
      // panel was open — hand focus to the declared stable trigger.
      document.querySelector(props.fallbackFocus)?.focus()
    }
  }
  openerEl = null
}

watch(() => props.modelValue, async(isOpen) => {
  if (isOpen) {
    enterStack()
    openerEl = document.activeElement
    await nextTick()
    adoptHeadingLabel()
    containerRef.value?.focus()
  } else {
    restoreFocus()
    leaveStack()
  }
}, { immediate: true })

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
  // Panels closed by unmount (parent v-if) never see modelValue flip. The
  // stack exit itself is the composable's own onBeforeUnmount.
  if (props.modelValue) restoreFocus()
})

defineExpose({ close })
</script>
